
// Import statements
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import IntegrationLogo from './IntegrationLogo';
import IntegrationCard from './IntegrationCard';

// Integration metadata for easier scaling
const INTEGRATIONS = [
  {
    slug: 'mailchimp',
    name: 'Mailchimp',
    logo: '/integrations/mailchimp.png',
    formSchema: z.object({
      apiKey: z.string().min(1, 'API Key is required'),
      listId: z.string().min(1, 'List ID is required'),
    }),
    inputs: [
      {
        name: 'apiKey',
        label: 'API Key',
        placeholder: 'Enter your Mailchimp API key',
        description: 'Find your API key in your Mailchimp account settings.',
        type: 'text',
      },
      {
        name: 'listId',
        label: 'List ID',
        placeholder: 'Enter your audience list ID',
        description: 'The ID of the audience list you want to sync with.',
        type: 'text',
      },
    ],
    connectedFields: [
      { label: 'API Key', value: 'apiKey', mask: true },
      { label: 'List ID', value: 'listId' },
    ],
    features: [
      "Automatically sync contacts and referrers",
      "Create targeted email campaigns for referral programs",
      "Track email campaign performance with Mailchimp analytics"
    ]
  },
  {
    slug: 'marketo',
    name: 'Marketo',
    logo: '/integrations/marketo.png',
    formSchema: z.object({
      clientId: z.string().min(1, 'Client ID is required'),
      clientSecret: z.string().min(1, 'Client Secret is required'),
      instanceUrl: z.string().url('Please enter a valid URL').min(1, 'Instance URL is required'),
    }),
    inputs: [
      {
        name: 'clientId',
        label: 'Client ID',
        placeholder: 'Enter your Marketo Client ID',
        description: 'Found in your Marketo Admin → LaunchPoint → API service.',
        type: 'text',
      },
      {
        name: 'clientSecret',
        label: 'Client Secret',
        placeholder: 'Enter your Marketo Client Secret',
        description: 'Found in your Marketo Admin → LaunchPoint → API service.',
        type: 'password',
      },
      {
        name: 'instanceUrl',
        label: 'Instance URL',
        placeholder: 'https://123-ABC-456.mktorest.com',
        description: 'Your Marketo REST API endpoint URL.',
        type: 'text',
      },
    ],
    connectedFields: [
      { label: 'Client ID', value: 'clientId', mask: true },
      { label: 'Instance URL', value: 'instanceUrl' },
    ],
    features: [
      "Automatically sync referrers as Marketo leads",
      "Create targeted drip campaigns for referral programs",
      "Track lead engagement and campaign performance"
    ]
  }
];

interface EmailMarketingIntegrationsProps {
  onSaved: () => Promise<void>;
}

const EmailMarketingIntegrations: React.FC<EmailMarketingIntegrationsProps> = ({ onSaved }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(INTEGRATIONS[0].slug);
  const [isConnected, setIsConnected] = useState<{ [key: string]: boolean }>({
    mailchimp: false,
    marketo: false
  });
  const [isTesting, setIsTesting] = useState<{ [key: string]: boolean }>({});

  // React Hook Form instances
  const mailchimpForm = useForm<z.infer<typeof INTEGRATIONS[0]["formSchema"]>>({
    resolver: zodResolver(INTEGRATIONS[0].formSchema),
    defaultValues: { apiKey: '', listId: '' }
  });
  
  const marketoForm = useForm<z.infer<typeof INTEGRATIONS[1]["formSchema"]>>({
    resolver: zodResolver(INTEGRATIONS[1].formSchema),
    defaultValues: { clientId: '', clientSecret: '', instanceUrl: '' }
  });

  // Map by slug for convenience
  const formBySlug: any = {
    mailchimp: mailchimpForm,
    marketo: marketoForm
  };

  // Simulate API calls
  const simulateApi = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleTestConnection = async (slug: string) => {
    const form = formBySlug[slug];
    const isValid = await form.trigger();
    if (!isValid) return;
    setIsTesting((old: any) => ({ ...old, [slug]: true }));
    try {
      await simulateApi(1500);
      toast({
        title: "Connection Successful",
        description: `Successfully connected to ${INTEGRATIONS.find(i => i.slug === slug)!.name}.`,
      });
      setIsConnected((old) => ({ ...old, [slug]: true }));
    } catch (e) {
      toast({
        title: "Connection Failed",
        description: `Failed to connect to ${INTEGRATIONS.find(i => i.slug === slug)!.name}. Please check your credentials.`,
        variant: "destructive",
      });
    } finally {
      setIsTesting((old: any) => ({ ...old, [slug]: false }));
    }
  };

  const handleSaveConfig = async (slug: string, data: any) => {
    try {
      await simulateApi(1000);
      toast({
        title: "Settings Saved",
        description: `${INTEGRATIONS.find(i => i.slug === slug)!.name} integration settings have been saved.`,
      });
      await onSaved();
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to save ${INTEGRATIONS.find(i => i.slug === slug)!.name} settings.`,
        variant: "destructive",
      });
    }
  };

  const handleDisconnect = async (slug: string) => {
    try {
      await simulateApi(800);
      setIsConnected((old) => ({ ...old, [slug]: false }));
      formBySlug[slug].reset();
      toast({
        title: "Disconnected",
        description: `Successfully disconnected from ${INTEGRATIONS.find(i => i.slug === slug)!.name}.`,
      });
      await onSaved();
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to disconnect from ${INTEGRATIONS.find(i => i.slug === slug)!.name}.`,
        variant: "destructive",
      });
    }
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className={`mb-4 grid w-full max-w-md grid-cols-${INTEGRATIONS.length}`}>
        {INTEGRATIONS.map(integration => (
          <TabsTrigger 
            value={integration.slug} 
            key={integration.slug} 
            className="flex items-center space-x-2"
          >
            <IntegrationLogo logoSrc={integration.logo} name={integration.name} size={20} className="h-5" />
            <span>{integration.name}</span>
          </TabsTrigger>
        ))}
      </TabsList>
      {INTEGRATIONS.map(integration => (
        <TabsContent value={integration.slug} key={integration.slug}>
          <IntegrationCard
            integration={integration}
            isConnected={isConnected[integration.slug]}
            isTesting={!!isTesting[integration.slug]}
            form={formBySlug[integration.slug]}
            onSaveConfig={(data) => handleSaveConfig(integration.slug, data)}
            onTestConnection={handleTestConnection}
            onDisconnect={handleDisconnect}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default EmailMarketingIntegrations;
